import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCookieBite, FaTimes, FaCog } from 'react-icons/fa';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    advertising: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences'));
        if (savedPreferences) {
          setPreferences(savedPreferences);
        }
      } catch (error) {
        console.error('Error loading cookie preferences:', error);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      advertising: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      advertising: false,
      functional: false
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
    setShowBanner(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Here you would typically trigger your analytics/advertising scripts based on preferences
    if (prefs.analytics) {
      // Enable Google Analytics
      console.log('Analytics cookies enabled');
    }
    if (prefs.advertising) {
      // Enable Google Ads
      console.log('Advertising cookies enabled');
    }
    if (prefs.functional) {
      // Enable functional cookies
      console.log('Functional cookies enabled');
    }
  };

  const handlePreferenceChange = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <>
      {/* Main Cookie Banner */}
      {showBanner && (
        <div className="cookie-banner">
          <div className="cookie-banner-content">
            <div className="cookie-icon">
              <FaCookieBite />
            </div>
            <div className="cookie-text">
              <h3>We use cookies to enhance your experience</h3>
              <p>
                We use cookies and similar technologies to help personalize content, 
                provide better user experience, and analyze our traffic. By clicking 
                "Accept All", you consent to our use of cookies. 
                <Link to="/cookie-policy" className="cookie-policy-link">
                  Learn more
                </Link>
              </p>
            </div>
            <div className="cookie-actions">
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={openSettings}
              >
                <FaCog /> Settings
              </button>
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={handleAcceptEssential}
              >
                Essential Only
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
            <button 
              className="cookie-close"
              onClick={handleAcceptEssential}
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-settings-overlay">
          <div className="cookie-settings-modal">
            <div className="cookie-settings-header">
              <h2>Cookie Settings</h2>
              <button className="cookie-close" onClick={closeSettings}>
                <FaTimes />
              </button>
            </div>
            
            <div className="cookie-settings-content">
              <p>
                Manage your cookie preferences. Essential cookies are necessary for the website to function properly.
              </p>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Essential Cookies</h3>
                    <p>Required for the website to function properly</p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.essential} 
                      disabled 
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Analytics Cookies</h3>
                    <p>Help us understand how visitors interact with our website</p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Advertising Cookies</h3>
                    <p>Used to deliver relevant advertisements and track campaign performance</p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.advertising}
                      onChange={() => handlePreferenceChange('advertising')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Functional Cookies</h3>
                    <p>Enable enhanced functionality and personalization</p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.functional}
                      onChange={() => handlePreferenceChange('functional')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div className="cookie-settings-footer">
              <Link to="/cookie-policy" className="cookie-policy-link">
                View Cookie Policy
              </Link>
              <div className="cookie-settings-actions">
                <button 
                  className="cookie-btn cookie-btn-secondary"
                  onClick={closeSettings}
                >
                  Cancel
                </button>
                <button 
                  className="cookie-btn cookie-btn-primary"
                  onClick={handleSavePreferences}
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent; 